import {
  Alert,
  Button,
  ButtonGroup,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';
import {Grid, Show, VStack} from 'platform/foundation';

import {isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {useListWarrantyTypeQuery} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';

import {getApiDateTimeString, parseDate, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {VehicleWarrantyRow} from '../../core/schema/VehicleWarrantyRowSchema';
import {
  getVehicleWarrantyRowFormSchema,
  VehicleWarrantyRowFormType,
} from '../utils/getVehicleWarrantyRowFormSchema';

interface VehicleWarrantyRowFormProps extends RequiredTestIdProps {
  // Products already used by the other rows; a product can be assigned to a vehicle only once.
  existingProductIds: string[];
  // When set, the form edits that row; the product cannot be changed.
  initialValue?: VehicleWarrantyRow;
  onSubmit: (row: VehicleWarrantyRow) => void;
  onDiscard: () => void;
}

// Local counterpart of the vehicle-detail warranty dialog: collects a row for a vehicle that does
// not exist yet, so it never calls the warranty API itself.
export function VehicleWarrantyRowForm(props: VehicleWarrantyRowFormProps) {
  const isEditing = isNotNil(props.initialValue);

  const {
    data: warrantyTypes,
    isLoading: areWarrantyTypesLoading,
    isError: isWarrantyTypesError,
    refetch: refetchWarrantyTypes,
  } = useListWarrantyTypeQuery();

  const warrantyProductOptions =
    warrantyTypes
      ?.filter((warrantyType) => !props.existingProductIds.includes(warrantyType.id))
      .map((warrantyType) => ({
        label: warrantyType.description,
        value: warrantyType.id,
      })) ?? [];

  const hasNoAvailableWarrantyTypes =
    !isEditing && !areWarrantyTypesLoading && isNilOrEmpty(warrantyProductOptions);

  // The row keeps the API date-time string so it can be posted as-is after the vehicle is created.
  const handleSubmit: FormSubmitHandler<VehicleWarrantyRowFormType> = (data) => {
    props.onSubmit({
      warrantyProductId: data.warrantyProductId,
      startDate: getApiDateTimeString(data.startDate),
      endDate: isNotNil(data.endDate) ? getApiDateTimeString(data.endDate) : null,
      startOdometerKm: data.startOdometerKm,
      endOdometerKm: data.endOdometerKm,
    });

    return Promise.resolve();
  };

  const initialEndDate = props.initialValue?.endDate;

  const defaultValues = {
    warrantyProductId: props.initialValue?.warrantyProductId,
    startDate: isNotNil(props.initialValue) ? parseDate(props.initialValue.startDate) : undefined,
    endDate: isNotNil(initialEndDate) ? parseDate(initialEndDate) : null,
    startOdometerKm: props.initialValue?.startOdometerKm ?? null,
    endOdometerKm: props.initialValue?.endOdometerKm ?? null,
  };

  // Without this, a failed request would look like an empty list and show the "all types used" alert.
  return (
    <DataStatus
      isError={isWarrantyTypesError}
      errorAction={{
        title: i18n.t('general.actions.tryAgain'),
        onClick: () => void refetchWarrantyTypes(),
      }}
      minHeight={60}
      data-testid={suffixTestId('warrantyTypes', props)}
    >
      <Form<VehicleWarrantyRowFormType>
        defaultValues={defaultValues}
        experimentalZodSchema={getVehicleWarrantyRowFormSchema(props.existingProductIds)}
        onSubmit={handleSubmit}
      >
        {(control) => (
          <VStack spacing={4}>
            <Show when={hasNoAvailableWarrantyTypes}>
              <Alert
                variant="info"
                title={i18n.t('entity.vehicleWarranty.notifications.allWarrantyTypesUsed')}
                hyperlinks={[
                  {
                    title: i18n.t('entity.warrantyType.actions.createNewWarrantyType'),
                    leftIcon: 'navigation/arrow_forward',
                    // New tab: in-tab navigation unmounts the unsaved create wizard and loses its data.
                    href: settingsRoutes.warrantyTypesCreate,
                    target: '_blank',
                  },
                ]}
                data-testid={suffixTestId('allWarrantyTypesUsed', props)}
              />
            </Show>
            <FormField
              control={control}
              type="choice"
              name="warrantyProductId"
              label={i18n.t('entity.warrantyType.labels.warrantyType')}
              options={warrantyProductOptions}
              isLoading={areWarrantyTypesLoading}
              isRequired
              isDisabled={isEditing || hasNoAvailableWarrantyTypes}
              data-testid={suffixTestId('warrantyProduct', props)}
            />
            <Grid columns={2} spacing={4}>
              <FormField
                control={control}
                type="date"
                name="startDate"
                label={i18n.t('entity.vehicle.labels.startDate')}
                isRequired
                data-testid={suffixTestId('startDate', props)}
              />
              <FormField
                control={control}
                type="date"
                name="endDate"
                label={i18n.t('entity.vehicle.labels.endDate')}
                data-testid={suffixTestId('endDate', props)}
              />
              <FormField
                control={control}
                type="integer"
                name="startOdometerKm"
                label={i18n.t('entity.vehicleWarranty.labels.startOdometer')}
                suffix={i18n.t('general.metric.km')}
                data-testid={suffixTestId('startOdometer', props)}
              />
              <FormField
                control={control}
                type="integer"
                name="endOdometerKm"
                label={i18n.t('entity.vehicleWarranty.labels.endOdometer')}
                suffix={i18n.t('general.metric.km')}
                data-testid={suffixTestId('endOdometer', props)}
              />
            </Grid>
            <ButtonGroup align="right">
              <Button
                title={i18n.t('general.actions.discard')}
                variant="secondary"
                onClick={props.onDiscard}
                data-testid={suffixTestId('discard', props)}
              />
              <FormButton
                control={control}
                title={i18n.t('general.actions.save')}
                type="submit"
                variant="primary"
                isDisabled={hasNoAvailableWarrantyTypes}
                data-testid={suffixTestId('submit', props)}
              />
            </ButtonGroup>
          </VStack>
        )}
      </Form>
    </DataStatus>
  );
}
